import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

import { getConsumer } from "@shared/lib/action-cable/consumer"

/**
 * Agent Run Live Controller — Stimulus controller for the Managed Agents
 * session-detail page (admin/ai/agent_runs/show).
 *
 * Subscribes to AgentRunChannel for a single run's step/status stream, so a
 * run in progress updates the page without a manual reload. Status changes
 * patch the badge in place (cheap, no layout to recompute). A new step is a
 * Turbo reload instead of an in-place DOM append: the transcript is a
 * Ui::Observability::WaterfallComponent instance, whose bars/panels/indices
 * are derived from the full step set (proportional widths depend on every
 * span's offset relative to the whole trace) — patching in one more span
 * client-side would mean re-deriving that layout in JS. A reload keeps the
 * server as the single source of truth for it.
 */
export default class extends Controller {
  static targets = ["statusBadge", "streamStatus"]

  // CONEK-6933: the server renders the pill's initial label through t(), then
  // the first cable callback overwrote it with English. The copy is supplied as
  // Stimulus values rather than looked up here, so no i18n key ever lives in
  // JS; the English literal stays as the fallback so a missing value can never
  // blank the label out.
  static values = {
    runId: String,
    spaceGroupId: String,
    liveLabel: String,
    disconnectedLabel: String,
    connectingLabel: String,
  }

  connect() {
    this.cable = getConsumer(this.spaceGroupIdValue || "default")
    this.subscription = null
    this.subscribe()
  }

  disconnect() {
    // Unsubscribe only — this.cable is the page's shared consumer; tearing it
    // down would cut the socket for every other controller using it.
    this.subscription?.unsubscribe()
  }

  subscribe() {
    if (!this.hasRunIdValue) return

    this.subscription = this.cable.subscriptions.create(
      { channel: "AgentRunChannel", run_id: this.runIdValue },
      {
        connected: () => this.setStreamStatus("connected"),
        disconnected: () => this.setStreamStatus("disconnected"),
        rejected: () => this.setStreamStatus("disconnected"),
        received: (data) => this.handleBroadcast(data),
      },
    )
  }

  setStreamStatus(status) {
    if (!this.hasStreamStatusTarget) return
    const target = this.streamStatusTarget
    target.dataset.status = status
    const dot = target.querySelector("span:first-child")
    const label = target.querySelector("span:last-child")
    if (status === "connected") {
      dot?.classList.remove("bg-amber-500", "bg-red-500")
      dot?.classList.add("bg-green-500")
      if (label) label.textContent = this.liveLabelValue || "Live"
    } else if (status === "disconnected") {
      dot?.classList.remove("bg-amber-500", "bg-green-500")
      dot?.classList.add("bg-red-500")
      if (label)
        label.textContent = this.disconnectedLabelValue || "Disconnected"
    } else {
      dot?.classList.remove("bg-green-500", "bg-red-500")
      dot?.classList.add("bg-amber-500")
      if (label) label.textContent = this.connectingLabelValue || "Connecting…"
    }
  }

  handleBroadcast(data) {
    if (data.type === "step_created") {
      this.handleStepCreated(data)
    } else if (data.type === "status_changed") {
      this.handleStatusChanged(data)
    }
  }

  handleStepCreated(data) {
    // See the class doc comment for why this is a reload rather than an
    // in-place DOM append.
    Turbo.visit(window.location.toString(), { action: "replace" })
    this.dispatch("stepCreated", { detail: data })
  }

  handleStatusChanged(data) {
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.dataset.status = data.status
    }
    this.dispatch("statusChanged", { detail: data })
  }
}
